import { Fragment, useEffect, useRef, useState } from "react"
import { Link } from "react-router-dom"
import { formatDeliveryMonths } from "../../lib/formatMonths.js"
import { MARKET_CAP_TIER_LABELS, marketCapTierFromValue } from "../../lib/marketCapTier.js"
import { RELEVANCE_LEVEL_LABELS, relevanceLevelFromScore } from "../../lib/relevance.js"
import styles from "./AdminPage.module.css"
import { GapSyncPanel } from "./GapSyncPanel.js"
import type { AdminCompany, AdminStats, CrawlDebugResult, JobEvent } from "./useAdmin.js"
import { PAGE_SIZE, useAdminCompanies, useAdminStats, useJobEvents } from "./useAdmin.js"

type StatsRelevantFields = Pick<
  AdminCompany,
  | "officialUrl"
  | "crawlStatus"
  | "noBenefit"
  | "isDiscontinued"
  | "noUrl"
  | "benefitActive"
  | "urlSearchFailedAt"
  | "externalBenefitUrlFailedAt"
  | "needsReview"
  | "businessDescriptionUnclear"
>

function isNotCrawled(c: StatsRelevantFields): boolean {
  return c.crawlStatus == null || c.crawlStatus === "pending"
}

function isSearchFailed(c: StatsRelevantFields): boolean {
  return c.urlSearchFailedAt != null && c.officialUrl == null
}

function jobTypeLabel(jobType: string): string {
  switch (jobType) {
    case "crawl":
      return "クロール"
    case "edinet_sync":
      return "EDINET財務"
    case "gap_sync":
      return "漏れURL取得"
    case "url_discovery":
      return "URL取得"
    default:
      return jobType
  }
}

function formatJobEventForCopy(ev: JobEvent): string {
  return [
    `[${new Date(ev.createdAt).toISOString()}] ${jobTypeLabel(ev.jobType)} / ${ev.status === "success" ? "成功" : "エラー"}`,
    ev.companyId ? `${ev.companyId} ${ev.companyName ?? ""}`.trim() : "",
    ev.jobType === "crawl" && ev.officialUrl ? ev.officialUrl : "",
    ev.message,
  ]
    .filter((line) => line.length > 0)
    .join("\n")
}

function ClipboardIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        fill="currentColor"
        d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 18H8V7h11v16z"
      />
    </svg>
  )
}

// Computes how the top stat cards should shift given a company's fields
// before/after a local mutation, so the UI doesn't need to re-fetch /stats.
function computeStatsDelta(
  prev: StatsRelevantFields,
  next: StatsRelevantFields
): Partial<Record<keyof AdminStats, number>> {
  const delta: Partial<Record<keyof AdminStats, number>> = {}
  const add = (key: keyof AdminStats, v: number) => {
    if (v !== 0) delta[key] = (delta[key] ?? 0) + v
  }
  add("withUrl", (next.officialUrl != null ? 1 : 0) - (prev.officialUrl != null ? 1 : 0))
  add("notCrawled", (isNotCrawled(next) ? 1 : 0) - (isNotCrawled(prev) ? 1 : 0))
  add("errors", (next.crawlStatus === "error" ? 1 : 0) - (prev.crawlStatus === "error" ? 1 : 0))
  add("benefitAcquired", (next.benefitActive ? 1 : 0) - (prev.benefitActive ? 1 : 0))
  add("noBenefit", (next.noBenefit ? 1 : 0) - (prev.noBenefit ? 1 : 0))
  add("flagNoUrl", (next.noUrl ? 1 : 0) - (prev.noUrl ? 1 : 0))
  add("discontinued", (next.isDiscontinued ? 1 : 0) - (prev.isDiscontinued ? 1 : 0))
  add("needsReview", (next.needsReview ? 1 : 0) - (prev.needsReview ? 1 : 0))
  add(
    "businessDescriptionUnclear",
    (next.businessDescriptionUnclear ? 1 : 0) - (prev.businessDescriptionUnclear ? 1 : 0)
  )
  add("searchFailed", (isSearchFailed(next) ? 1 : 0) - (isSearchFailed(prev) ? 1 : 0))
  return delta
}

const COLUMN_KEYS = ["name", "benefit", "financials", "flags", "edit"] as const
type ColumnKey = (typeof COLUMN_KEYS)[number]

const COLUMN_LABELS: Record<ColumnKey, string> = {
  name: "会社名",
  benefit: "優待情報",
  financials: "財務(EDINET概算)",
  flags: "フラグ",
  edit: "URL編集",
}

function loadColVisibility(): Record<ColumnKey, boolean> {
  const defaults: Record<ColumnKey, boolean> = {
    name: true,
    benefit: true,
    financials: true,
    flags: true,
    edit: true,
  }
  try {
    const stored = localStorage.getItem("admin-col-visibility")
    if (stored) {
      return { ...defaults, ...(JSON.parse(stored) as Partial<Record<ColumnKey, boolean>>) }
    }
  } catch {}
  return defaults
}

function getRowClass(company: AdminCompany, s: typeof styles): string {
  if (company.crawlStatus === "error") return s.rowError
  if (company.isDiscontinued) return s.rowDiscontinued
  if (company.noBenefit) return s.rowNoBenefit
  return ""
}

function formatPercent(count: number, total: number): string {
  if (total <= 0) return "0.0%"
  return `${((count / total) * 100).toFixed(1)}%`
}

// EDINETの逆算株価は「事業年度末時点」の水準であり、現在の実勢株価ではない —
// 誤認防止のため、算出根拠となった決算期を明示する（免責事項ページも参照）。
function formatFiscalPeriodEnd(dateStr: string | null): string | null {
  if (!dateStr) return null
  const d = new Date(dateStr)
  if (Number.isNaN(d.getTime())) return null
  return `${d.getFullYear()}年${d.getMonth() + 1}月期末時点の概算`
}

// urlなし = simply "no official_url yet" — total minus urlあり. エラー
// (stats.searchFailed) is a subset of this: companies with no URL AND a
// recorded automatic-search failure.
function urlMissingCount(stats: AdminStats): number {
  return Math.max(0, stats.total - stats.withUrl)
}

// クロール実施済み = a crawl landed on one of these 4 concrete outcomes.
function crawlDoneCount(stats: AdminStats): number {
  return stats.benefitAcquired + stats.discontinued + stats.noBenefit + stats.errors
}

// クロール未実施 = has a URL but hasn't landed on any of the 4 crawl-done
// outcomes yet — derived by subtraction (not crawl_status directly) so it
// always agrees with the crawlDoneCount breakdown shown alongside it.
function crawlNotDoneCount(stats: AdminStats): number {
  return Math.max(0, stats.withUrl - crawlDoneCount(stats))
}

// EDINET財務 未取得 = 優待情報取得済みの会社のうち、取得済み・取得不可・
// エラーのいずれにも該当しないもの — 差分で出すことで内訳と必ず一致する。
function edinetPendingCount(stats: AdminStats): number {
  return Math.max(
    0,
    stats.benefitAcquired - stats.edinetFetched - stats.edinetUnavailable - stats.edinetErrors
  )
}

// A stat card showing a count. Renders as a button (wired to the filter
// dropdown) when onClick is given, or a plain div for derived counts that
// have no matching single filter.
function ProgressStatCard({
  label,
  count,
  active,
  onClick,
}: {
  label: string
  count: number
  active?: boolean
  onClick?: () => void
}) {
  const content = (
    <>
      <span className={styles.statNum}>{count.toLocaleString()}</span>
      <span className={styles.statLabel}>{label}</span>
    </>
  )
  const className = `${styles.statCard} ${styles.statCardCompleted} ${active ? styles.statCardActive : ""}`
  if (!onClick) {
    return <div className={className}>{content}</div>
  }
  return (
    <button type="button" className={className} onClick={onClick}>
      {content}
    </button>
  )
}

export function AdminPage() {
  const {
    stats,
    loading: statsLoading,
    error: statsError,
    load: loadStats,
    adjustStats,
  } = useAdminStats()
  const {
    items,
    total,
    page,
    loading,
    error: companiesError,
    hasUrlFilter,
    load,
    setUrl,
    setFlags,
    searchUrls,
    fetchCompany,
    triggerCrawl,
    fetchCrawlStatus,
    debugCrawl,
    cleanupNonIndividual,
    publishBenefits,
    triggerEdinetSync,
    discoverUrl,
    discoverUrlQuiet,
    fetchCompaniesWithoutUrl,
    fetchCompaniesWithUrl,
    updateItemUrl,
    updateItemFlag,
    updateItem,
    changeFilter,
    changePage,
  } = useAdminCompanies()

  const [editingId, setEditingId] = useState<string | null>(null)
  const [editUrl, setEditUrl] = useState("")
  const [saving, setSaving] = useState(false)
  const [candidateSearchingId, setCandidateSearchingId] = useState<string | null>(null)
  const [candidates, setCandidates] = useState<{
    companyId: string
    results: { title: string; url: string }[]
    debug: string
    aiRecommended: string | null
    rawExaResponse?: string
  } | null>(null)
  const [crawlDebug, setCrawlDebug] = useState<{
    companyId: string
    result: CrawlDebugResult | null
  } | null>(null)
  const [crawlDebugLoadingId, setCrawlDebugLoadingId] = useState<string | null>(null)
  const [message, setMessage] = useState("")
  const [autoRunning, setAutoRunning] = useState(false)
  const [autoProgress, setAutoProgress] = useState<{
    done: number
    total: number
    found: number
    currentName: string
  } | null>(null)
  const [crawlingIds, setCrawlingIds] = useState<Set<string>>(new Set())
  const stopRef = useRef(false)
  const crawlPollingRef = useRef<Map<string, ReturnType<typeof setInterval>>>(new Map())
  const [crawlAllRunning, setCrawlAllRunning] = useState(false)
  const [crawlAllProgress, setCrawlAllProgress] = useState<{
    done: number
    total: number
    currentName: string
    counts: { benefitAcquired: number; noBenefit: number; discontinued: number; error: number }
  } | null>(null)
  const [crawlAllError, setCrawlAllError] = useState<string | null>(null)
  const [crawlAllLog, setCrawlAllLog] = useState<
    { id: string; name: string; officialUrl: string | null; result: string }[]
  >([])
  const crawlAllStopRef = useRef(false)
  const wakeLockRef = useRef<WakeLockSentinel | null>(null)
  const wakeLockUsersRef = useRef(0)
  const [showMoreMenu, setShowMoreMenu] = useState(false)
  const {
    events: jobEvents,
    loading: jobEventsLoading,
    error: jobEventsError,
    jobType: jobEventType,
    load: loadJobEvents,
    changeType: changeJobEventType,
  } = useJobEvents()
  const [showJobLog, setShowJobLog] = useState(false)

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional mount-only fetch
  useEffect(() => {
    loadStats()
    load()
    return () => {
      for (const t of crawlPollingRef.current.values()) clearInterval(t)
    }
  }, [])

  // biome-ignore lint/correctness/useExhaustiveDependencies: fetch + poll only while the log panel is open
  useEffect(() => {
    if (!showJobLog) return
    loadJobEvents()
    const timer = setInterval(() => loadJobEvents(), 30_000)
    return () => clearInterval(timer)
  }, [showJobLog])

  async function handleAutoDiscover() {
    if (autoRunning) {
      stopRef.current = true
      return
    }
    stopRef.current = false
    setAutoRunning(true)
    setAutoProgress(null)
    await acquireWakeLock()
    try {
      const companies = await fetchCompaniesWithoutUrl()
      const total = companies.length
      let found = 0
      for (let i = 0; i < companies.length; i++) {
        if (stopRef.current) break
        const company = companies[i]
        setAutoProgress({ done: i, total, found, currentName: company.name })
        try {
          const url = await discoverUrlQuiet(company.id)
          if (url) {
            found++
            updateItemUrl(company.id, url)
            adjustStats(
              computeStatsDelta(company, { ...company, officialUrl: url, crawlStatus: "pending" })
            )
          } else {
            adjustStats(computeStatsDelta(company, { ...company, urlSearchFailedAt: Date.now() }))
          }
        } catch {}
      }
      setAutoProgress(null)
      await load()
      if (!stopRef.current) {
        setMessage(`✓ 順次URL取得完了: ${found}/${companies.length} 件取得`)
        setTimeout(() => setMessage(""), 5000)
      }
    } catch (e) {
      setMessage(`✗ エラー: ${e}`)
    } finally {
      await releaseWakeLock()
      setAutoRunning(false)
      setAutoProgress(null)
    }
  }

  // Keeps the screen from sleeping (Safari on iOS 16.4+) while either
  // sequential run (URL discovery or crawl) is active. navigator.wakeLock
  // .request() must happen synchronously-ish within a user-gesture call
  // stack — requesting it from a useEffect keyed on a "running" state flag
  // (the previous approach) fires after React's re-render, outside that
  // gesture window, and Safari silently drops such requests. So acquire()
  // must be awaited directly inside the click handler, as its first async
  // step, not reactively. wakeLockUsersRef lets the two independent runs
  // share one sentinel without one finishing early releasing it out from
  // under the other.
  async function requestWakeLockSentinel() {
    if (!("wakeLock" in navigator) || wakeLockRef.current) return
    try {
      wakeLockRef.current = await navigator.wakeLock.request("screen")
    } catch {
      // Not fatal — the run still proceeds, it just won't keep the screen awake.
    }
  }

  async function acquireWakeLock() {
    wakeLockUsersRef.current++
    await requestWakeLockSentinel()
  }

  async function releaseWakeLock() {
    wakeLockUsersRef.current = Math.max(0, wakeLockUsersRef.current - 1)
    if (wakeLockUsersRef.current > 0) return
    try {
      await wakeLockRef.current?.release()
    } catch {}
    wakeLockRef.current = null
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: mount-only; reads the ref at event time instead of depending on run-state
  useEffect(() => {
    // The OS silently drops the lock when the tab/app is backgrounded (e.g.
    // switching apps briefly) — re-acquire it once the tab is visible again,
    // if a run is still in progress.
    function handleVisibilityChange() {
      if (document.visibilityState === "visible" && wakeLockUsersRef.current > 0) {
        requestWakeLockSentinel()
      }
    }
    document.addEventListener("visibilitychange", handleVisibilityChange)
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange)
  }, [])

  // Polls crawl-status until it leaves 'pending', capped so one stuck
  // company can't hang the whole sequential run indefinitely.
  async function waitForCrawlDone(
    id: string,
    timeoutMs = 90_000
  ): Promise<{ crawlStatus: string | null; errorCount: number; lastError: string | null }> {
    const start = Date.now()
    while (Date.now() - start < timeoutMs) {
      await new Promise((resolve) => setTimeout(resolve, 3000))
      const status = await fetchCrawlStatus(id)
      if (status.crawlStatus !== "pending") return status
    }
    return { crawlStatus: "error", errorCount: 0, lastError: "タイムアウトしました" }
  }

  async function handleAutoCrawl() {
    if (crawlAllRunning) {
      crawlAllStopRef.current = true
      return
    }
    crawlAllStopRef.current = false
    setCrawlAllRunning(true)
    setCrawlAllProgress(null)
    setCrawlAllError(null)
    setCrawlAllLog([])
    await acquireWakeLock()
    let lastErrorMessage: string | null = null
    let consecutiveSameError = 0
    let stopReason: string | null = null
    let companies: AdminCompany[] = []
    const counts = { benefitAcquired: 0, noBenefit: 0, discontinued: 0, error: 0 }
    try {
      companies = await fetchCompaniesWithUrl()
      for (let i = 0; i < companies.length; i++) {
        if (crawlAllStopRef.current) break
        const company = companies[i]
        setCrawlAllProgress({
          done: i,
          total: companies.length,
          currentName: company.name,
          counts: { ...counts },
        })
        setCrawlingIds((prev) => new Set([...prev, company.id]))
        try {
          await triggerCrawl(company.id)
          const status = await waitForCrawlDone(company.id)
          const fresh = await fetchCompany(company.id)
          updateItem(company.id, fresh)
          adjustStats(computeStatsDelta(company, fresh))

          let resultLabel: string
          if (fresh.isDiscontinued) {
            counts.discontinued++
            resultLabel = "廃止"
          } else if (fresh.noBenefit) {
            counts.noBenefit++
            resultLabel = "優待なし"
          } else if (fresh.benefitActive) {
            counts.benefitAcquired++
            resultLabel = "✓ 優待内容取得"
          } else if (status.crawlStatus === "error") {
            counts.error++
            resultLabel = `✗ エラー: ${status.lastError ?? ""}`
          } else {
            resultLabel = "変更なし"
          }
          setCrawlAllLog((prev) => [
            {
              id: company.id,
              name: company.name,
              officialUrl: fresh.officialUrl,
              result: resultLabel,
            },
            ...prev,
          ])

          if (status.crawlStatus === "error" && status.lastError) {
            if (status.lastError === lastErrorMessage) {
              consecutiveSameError++
            } else {
              lastErrorMessage = status.lastError
              consecutiveSameError = 1
            }
            if (consecutiveSameError >= 3) {
              stopReason = `同じエラーが3回連続したため停止しました: ${lastErrorMessage}`
            }
          } else {
            lastErrorMessage = null
            consecutiveSameError = 0
          }
        } catch (e) {
          counts.error++
          const msg = e instanceof Error ? e.message : String(e)
          setCrawlAllLog((prev) => [
            {
              id: company.id,
              name: company.name,
              officialUrl: company.officialUrl,
              result: `✗ エラー: ${msg}`,
            },
            ...prev,
          ])
          if (msg === lastErrorMessage) {
            consecutiveSameError++
          } else {
            lastErrorMessage = msg
            consecutiveSameError = 1
          }
          if (consecutiveSameError >= 3) {
            stopReason = `同じエラーが3回連続したため停止しました: ${lastErrorMessage}`
          }
        } finally {
          setCrawlingIds((prev) => {
            const next = new Set(prev)
            next.delete(company.id)
            return next
          })
        }
        setCrawlAllProgress({
          done: i + 1,
          total: companies.length,
          currentName: company.name,
          counts: { ...counts },
        })
        if (stopReason) break
      }
    } catch (e) {
      setMessage(`✗ エラー: ${e}`)
    } finally {
      await releaseWakeLock()
      setCrawlAllProgress(null)
      setCrawlAllRunning(false)
      if (stopReason) {
        setCrawlAllError(stopReason)
      } else if (!crawlAllStopRef.current && companies.length > 0) {
        setMessage(
          `✓ 順次クロール完了 (${companies.length}件) — 取得済み ${counts.benefitAcquired}件・優待なし ${counts.noBenefit}件・廃止 ${counts.discontinued}件・エラー ${counts.error}件`
        )
        setTimeout(() => setMessage(""), 8000)
      }
      await load()
      await loadStats()
    }
  }

  async function handleCopyCrawlAllError() {
    if (!crawlAllError) return
    try {
      await navigator.clipboard.writeText(crawlAllError)
      setMessage("✓ エラー内容をコピーしました")
      setTimeout(() => setMessage(""), 3000)
    } catch {
      setMessage("✗ コピーに失敗しました")
    }
  }

  async function handleCopyJobEvent(ev: JobEvent) {
    try {
      await navigator.clipboard.writeText(formatJobEventForCopy(ev))
      setMessage("✓ ログをコピーしました")
      setTimeout(() => setMessage(""), 3000)
    } catch {
      setMessage("✗ コピーに失敗しました")
    }
  }

  const [colVisible, setColVisible] = useState<Record<ColumnKey, boolean>>(loadColVisibility)

  function toggleCol(key: ColumnKey) {
    setColVisible((prev) => {
      const next = { ...prev, [key]: !prev[key] }
      localStorage.setItem("admin-col-visibility", JSON.stringify(next))
      return next
    })
  }

  const totalCols = COLUMN_KEYS.filter((k) => colVisible[k]).length

  async function handleSaveUrl(id: string) {
    setSaving(true)
    try {
      const before = items.find((i) => i.id === id)
      await setUrl(id, editUrl)
      if (before) {
        adjustStats(
          computeStatsDelta(before, { ...before, officialUrl: editUrl, crawlStatus: "pending" })
        )
      }
      setEditingId(null)
      setMessage(`✓ ${id} のURLを更新しました`)
      setTimeout(() => setMessage(""), 3000)
    } catch (e) {
      setMessage(`✗ エラー: ${e}`)
    } finally {
      setSaving(false)
    }
  }

  async function handleCleanup() {
    try {
      const deleted = await cleanupNonIndividual()
      setMessage(`✓ 個別株以外を ${deleted} 件削除しました`)
      if (deleted > 0) adjustStats({ total: -deleted })
      setTimeout(() => setMessage(""), 5000)
    } catch (e) {
      setMessage(`✗ エラー: ${e}`)
    }
  }

  async function handlePublish() {
    try {
      await publishBenefits()
      setMessage("✓ 公開JSONを再生成しました")
      setTimeout(() => setMessage(""), 5000)
    } catch (e) {
      setMessage(`✗ エラー: ${e}`)
    }
  }

  async function handleTriggerEdinet() {
    try {
      await triggerEdinetSync()
      setMessage("✓ EDINET財務バッチを実行しました（実行ログで結果を確認してください）")
      setTimeout(() => setMessage(""), 5000)
    } catch (e) {
      setMessage(`✗ エラー: ${e}`)
    }
  }

  async function handleTriggerOne(id: string) {
    setCrawlingIds((prev) => new Set([...prev, id]))
    try {
      const before = items.find((i) => i.id === id)
      await triggerCrawl(id)
      updateItem(id, { crawlStatus: "pending", errorCount: 0, lastError: null })
      const pending = before ? { ...before, crawlStatus: "pending", errorCount: 0 } : undefined
      if (before && pending) adjustStats(computeStatsDelta(before, pending))

      if (crawlPollingRef.current.has(id)) clearInterval(crawlPollingRef.current.get(id))

      const timer = setInterval(async () => {
        try {
          const status = await fetchCrawlStatus(id)
          updateItem(id, {
            crawlStatus: status.crawlStatus,
            errorCount: status.errorCount,
            lastError: status.lastError,
          })
          if (status.crawlStatus !== "pending") {
            clearInterval(timer)
            crawlPollingRef.current.delete(id)
            setCrawlingIds((prev) => {
              const next = new Set(prev)
              next.delete(id)
              return next
            })
            fetchCompany(id)
              .then((data) => {
                updateItem(id, data)
                if (pending) adjustStats(computeStatsDelta(pending, data))
              })
              .catch(() => {})
          }
        } catch {
          clearInterval(timer)
          crawlPollingRef.current.delete(id)
          setCrawlingIds((prev) => {
            const next = new Set(prev)
            next.delete(id)
            return next
          })
        }
      }, 3000)
      crawlPollingRef.current.set(id, timer)
    } catch (e) {
      setCrawlingIds((prev) => {
        const next = new Set(prev)
        next.delete(id)
        return next
      })
      setMessage(`✗ クロール開始エラー (${id}): ${e}`)
    }
  }

  async function handleRetrySearch(id: string) {
    try {
      const before = items.find((i) => i.id === id)
      const url = await discoverUrl(id)
      if (before) {
        const next = url
          ? { ...before, officialUrl: url, crawlStatus: "pending", urlSearchFailedAt: null }
          : { ...before, urlSearchFailedAt: Date.now() }
        adjustStats(computeStatsDelta(before, next))
      }
      setMessage(
        url ? `✓ ${id} のURLを取得しました: ${url}` : `✗ ${id} は再検索しても見つかりませんでした`
      )
      setTimeout(() => setMessage(""), 5000)
    } catch (e) {
      setMessage(`✗ エラー: ${e}`)
    }
  }

  async function handleSearchUrls(id: string) {
    setCandidateSearchingId(id)
    setCandidates(null)
    try {
      const { results, debug, aiRecommended, rawExaResponse } = await searchUrls(id)
      setCandidates({ companyId: id, results, debug, aiRecommended, rawExaResponse })
    } catch (e) {
      setMessage(`✗ エラー: ${e}`)
    } finally {
      setCandidateSearchingId(null)
    }
  }

  async function handleDebugCrawl(id: string) {
    if (crawlDebug?.companyId === id) {
      setCrawlDebug(null)
      return
    }
    setCrawlDebugLoadingId(id)
    setCrawlDebug(null)
    try {
      const result = await debugCrawl(id)
      setCrawlDebug({ companyId: id, result })
    } catch (e) {
      setCrawlDebug({ companyId: id, result: null })
      setMessage(`✗ デバッグ取得エラー: ${e}`)
    } finally {
      setCrawlDebugLoadingId(null)
    }
  }

  async function handleSelectCandidate(id: string, url: string) {
    try {
      const before = items.find((i) => i.id === id)
      await setUrl(id, url)
      if (before) {
        adjustStats(
          computeStatsDelta(before, { ...before, officialUrl: url, crawlStatus: "pending" })
        )
      }
      setCandidates(null)
      setMessage(`✓ ${id} のURLを登録しました`)
      setTimeout(() => setMessage(""), 3000)
    } catch (e) {
      setMessage(`✗ エラー: ${e}`)
    }
  }

  async function handleToggleNoUrl(company: AdminCompany) {
    try {
      const next = { ...company, noUrl: !company.noUrl }
      await setFlags(company.id, { noUrl: next.noUrl })
      updateItemFlag(company.id, { noUrl: next.noUrl })
      adjustStats(computeStatsDelta(company, next))
    } catch (e) {
      setMessage(`✗ エラー: ${e}`)
    }
  }

  async function handleToggleNoBenefit(company: AdminCompany) {
    try {
      const noBenefit = !company.noBenefit
      const next = {
        ...company,
        noBenefit,
        benefitActive: noBenefit ? false : company.benefitActive,
      }
      await setFlags(company.id, { noBenefit })
      updateItemFlag(company.id, { noBenefit })
      adjustStats(computeStatsDelta(company, next))
    } catch (e) {
      setMessage(`✗ エラー: ${e}`)
    }
  }

  async function handleToggleDiscontinued(company: AdminCompany) {
    try {
      if (company.isDiscontinued) {
        await setFlags(company.id, {
          isDiscontinued: false,
          discontinuedYear: null,
          discontinuedMonth: null,
        })
        updateItemFlag(company.id, { isDiscontinued: false })
        adjustStats(computeStatsDelta(company, { ...company, isDiscontinued: false }))
      } else {
        const yearStr = window.prompt("廃止年を入力 (例: 2024、不明なら空欄)")
        const monthStr = window.prompt("廃止月を入力 (1〜12、不明なら空欄)")
        const year = yearStr ? Number(yearStr) : null
        const month = monthStr ? Number(monthStr) : null
        await setFlags(company.id, {
          isDiscontinued: true,
          discontinuedYear: year && year > 2000 && year < 2100 ? year : null,
          discontinuedMonth: month && month >= 1 && month <= 12 ? month : null,
        })
        updateItemFlag(company.id, {
          isDiscontinued: true,
        })
        adjustStats(
          computeStatsDelta(company, { ...company, isDiscontinued: true, benefitActive: false })
        )
      }
    } catch (e) {
      setMessage(`✗ エラー: ${e}`)
    }
  }

  async function handleToggleNeedsReview(company: AdminCompany) {
    try {
      const needsReview = !company.needsReview
      await setFlags(company.id, { needsReview })
      updateItemFlag(company.id, { needsReview })
      adjustStats(computeStatsDelta(company, { ...company, needsReview }))
    } catch (e) {
      setMessage(`✗ エラー: ${e}`)
    }
  }

  async function handleToggleBusinessDescriptionUnclear(company: AdminCompany) {
    try {
      const businessDescriptionUnclear = !company.businessDescriptionUnclear
      await setFlags(company.id, { businessDescriptionUnclear })
      updateItemFlag(company.id, { businessDescriptionUnclear })
      adjustStats(computeStatsDelta(company, { ...company, businessDescriptionUnclear }))
    } catch (e) {
      setMessage(`✗ エラー: ${e}`)
    }
  }

  const totalPages = Math.ceil(total / PAGE_SIZE)

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.title}>管理画面</h1>
        <div className={styles.headerActions}>
          <Link to="/login/backdoor?returnTo=/lists" className={styles.backdoorLink}>
            裏口ログイン
          </Link>
          <a href="/" className={styles.backLink}>
            ← 公開サイトへ
          </a>
        </div>
      </div>

      <Link to="/admin/margin-inventory" className={styles.marginInventoryLink}>
        <span>一般信用在庫（楽天）</span>
        <span className={styles.marginInventoryLinkSub}>CSVアップロード・今月優待の在庫一覧 →</span>
      </Link>

      {/* API errors */}
      {(statsError || companiesError) && (
        <div
          style={{
            background: "#fee",
            border: "1px solid #f00",
            padding: "8px 12px",
            marginBottom: 12,
            borderRadius: 4,
            fontFamily: "monospace",
            fontSize: 13,
          }}
        >
          {statsError && <div>Stats error: {statsError}</div>}
          {companiesError && <div>Companies error: {companiesError}</div>}
          <div style={{ marginTop: 4, color: "#555" }}>
            WORKER_URL: {import.meta.env.VITE_WORKER_URL || "(empty)"}
          </div>
        </div>
      )}

      {/* Stats — click a card to filter the list below */}
      {stats && !statsLoading && (
        <>
          <div className={styles.stats}>
            {(
              [
                { value: "all", label: "全会社数", count: stats.total, warn: false },
                { value: "yes", label: "登録済み優待URL", count: stats.withUrl, warn: false },
                {
                  value: "searchFailed",
                  label: "未確認優待URL",
                  count: stats.searchFailed,
                  warn: true,
                },
                { value: "error", label: "クロールエラー", count: stats.errors, warn: true },
                {
                  value: "externalBenefitUrlFailed",
                  label: "外部優待URL不可",
                  count: stats.externalBenefitUrlFailed,
                  warn: true,
                },
                {
                  value: "needsReview",
                  label: "要確認(複数優待制度)",
                  count: stats.needsReview,
                  warn: true,
                },
                {
                  value: "businessDescriptionUnclear",
                  label: "事業内容不明",
                  count: stats.businessDescriptionUnclear,
                  warn: true,
                },
              ] as const
            ).map(({ value, label, count, warn }) => (
              <button
                key={value}
                type="button"
                className={`${styles.statCard} ${warn && count > 0 ? styles.statCardError : ""} ${hasUrlFilter === value ? styles.statCardActive : ""}`}
                onClick={() => changeFilter(value)}
              >
                <span className={styles.statNum}>{count.toLocaleString()}</span>
                <span className={styles.statLabel}>{label}</span>
              </button>
            ))}
          </div>

          {/* 優待URL取得の進捗 — urlあり(official_url IS NOT NULL)とurlなし
              (official_url IS NULL)の2択。エラーはurlなしの部分集合
              (url_search_failed_atが記録されているもの)。
              進捗% = (urlあり + エラー) / 全会社数 — 取得できた/自動検索を
              試みて失敗した、のいずれかで「決着がついた」割合。100%で
              「全社について取得を試みた」ことを意味する。 */}
          <div className={styles.completedGroup}>
            <span className={styles.completedGroupLabel}>
              優待URL取得の進捗（{formatPercent(stats.withUrl + stats.searchFailed, stats.total)}）
            </span>
            <div className={styles.completedGroupCards}>
              <ProgressStatCard
                label="urlあり"
                count={stats.withUrl}
                active={hasUrlFilter === "yes"}
                onClick={() => changeFilter("yes")}
              />
              <ProgressStatCard
                label="urlなし"
                count={urlMissingCount(stats)}
                active={hasUrlFilter === "no"}
                onClick={() => changeFilter("no")}
              />
              <ProgressStatCard
                label="エラー (urlなしのうち)"
                count={stats.searchFailed}
                active={hasUrlFilter === "searchFailed"}
                onClick={() => changeFilter("searchFailed")}
              />
            </div>
          </div>

          {/* クロールの進捗 — 分母は優待URLを取得済みの会社数。
              クロール実施済み = 優待情報取得済み(active) + 優待情報取得済み(廃止)
              + 優待情報なし + エラー の4outcomeの合計。クロール未実施はその残り
              (crawl_statusを直接見るのではなく分母からの差分で出すことで、この
              4つの内訳と必ず一致するようにしている)。進捗% = 実施済み/分母。 */}
          <div className={styles.completedGroup}>
            <span className={styles.completedGroupLabel}>
              クロールの進捗（{formatPercent(crawlDoneCount(stats), stats.withUrl)}）
            </span>
            <div className={styles.completedGroupCards}>
              <ProgressStatCard
                label="優待情報取得済み"
                count={stats.benefitAcquired}
                active={hasUrlFilter === "benefitAcquired"}
                onClick={() => changeFilter("benefitAcquired")}
              />
              <ProgressStatCard
                label="優待情報取得済み (廃止)"
                count={stats.discontinued}
                active={hasUrlFilter === "discontinued"}
                onClick={() => changeFilter("discontinued")}
              />
              <ProgressStatCard
                label="優待情報なし"
                count={stats.noBenefit}
                active={hasUrlFilter === "noBenefit"}
                onClick={() => changeFilter("noBenefit")}
              />
              <ProgressStatCard
                label="エラー"
                count={stats.errors}
                active={hasUrlFilter === "error"}
                onClick={() => changeFilter("error")}
              />
              <ProgressStatCard label="クロール未実施" count={crawlNotDoneCount(stats)} />
            </div>
          </div>

          {/* EDINET財務取得の進捗 — 分母は優待情報取得済みの会社数のみ
              (companiesテーブルは優待未登録の会社も含む全上場企業リストの
              ため、そちらを分母にすると意味がずれる)。進捗% =
              (取得済み + 取得不可) / 優待情報取得済み会社数 — 概算株価を
              算出できた・できない(赤字等)のいずれかで「決着がついた」割合。 */}
          <div className={styles.completedGroup}>
            <span className={styles.completedGroupLabel}>
              EDINET財務取得の進捗（
              {formatPercent(stats.edinetFetched + stats.edinetUnavailable, stats.benefitAcquired)}
              ）
            </span>
            <div className={styles.completedGroupCards}>
              <ProgressStatCard label="取得済み" count={stats.edinetFetched} />
              <ProgressStatCard label="取得不可 (赤字等)" count={stats.edinetUnavailable} />
              <ProgressStatCard label="エラー" count={stats.edinetErrors} />
              <ProgressStatCard label="未取得" count={edinetPendingCount(stats)} />
            </div>
          </div>
        </>
      )}

      {/* Actions */}
      <div className={styles.actions}>
        <div className={styles.actionBtns}>
          <button
            type="button"
            className={autoRunning ? styles.stopBtn : styles.triggerBtn}
            onClick={handleAutoDiscover}
          >
            {autoRunning ? "■ 停止" : "順次URL取得"}
          </button>
          <button
            type="button"
            className={crawlAllRunning ? styles.stopBtn : styles.triggerBtn}
            onClick={handleAutoCrawl}
          >
            {crawlAllRunning ? "■ 停止" : "順次クロール実行"}
          </button>
          <div className={styles.moreMenuWrapper}>
            <button
              type="button"
              className={styles.moreMenuBtn}
              onClick={() => setShowMoreMenu((v) => !v)}
              aria-label="その他の操作"
            >
              ⋯
            </button>
            {showMoreMenu && (
              <div className={styles.moreMenuDropdown}>
                <button
                  type="button"
                  className={styles.triggerBtn}
                  onClick={() => {
                    setShowMoreMenu(false)
                    handlePublish()
                  }}
                >
                  公開JSON再生成
                </button>
                <button
                  type="button"
                  className={styles.triggerBtn}
                  onClick={() => {
                    setShowMoreMenu(false)
                    handleCleanup()
                  }}
                >
                  個別株以外を削除
                </button>
                <button
                  type="button"
                  className={styles.triggerBtn}
                  onClick={() => {
                    setShowMoreMenu(false)
                    handleTriggerEdinet()
                  }}
                >
                  EDINET財務バッチを今すぐ実行
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <GapSyncPanel
        onMessage={(text) => {
          setMessage(text)
          if (text.startsWith("✓")) setTimeout(() => setMessage(""), 6000)
        }}
      />

      {/* Column visibility toggles */}
      <div className={styles.colToggles}>
        <span className={styles.colToggleLabel}>表示列:</span>
        {COLUMN_KEYS.map((key) => (
          <button
            key={key}
            type="button"
            className={`${styles.colToggleBtn} ${colVisible[key] ? styles.colToggleBtnActive : ""}`}
            onClick={() => toggleCol(key)}
          >
            {COLUMN_LABELS[key]}
          </button>
        ))}
      </div>

      {autoProgress && (
        <div className={styles.autoProgress}>
          <span className={styles.autoProgressBar}>
            <span
              className={styles.autoProgressFill}
              style={{ width: `${Math.round((autoProgress.done / autoProgress.total) * 100)}%` }}
            />
          </span>
          <span className={styles.autoProgressText}>
            {autoProgress.done}/{autoProgress.total} — 取得済み {autoProgress.found}件 —{" "}
            {autoProgress.currentName}
          </span>
        </div>
      )}

      {crawlAllProgress && (
        <div className={styles.autoProgress}>
          <span className={styles.autoProgressBar}>
            <span
              className={styles.autoProgressFill}
              style={{
                width: `${Math.round((crawlAllProgress.done / crawlAllProgress.total) * 100)}%`,
              }}
            />
          </span>
          <span className={styles.autoProgressText}>
            {crawlAllProgress.done}/{crawlAllProgress.total} — 取得済み{" "}
            {crawlAllProgress.counts.benefitAcquired}件 ・ 優待なし{" "}
            {crawlAllProgress.counts.noBenefit}件 ・ 廃止 {crawlAllProgress.counts.discontinued}件
            ・ エラー {crawlAllProgress.counts.error}件 — {crawlAllProgress.currentName}
          </span>
        </div>
      )}

      {crawlAllError && (
        <div className={styles.crawlAllErrorBanner}>
          <div className={styles.crawlAllErrorText}>
            順次クロールがエラーで停止しました
            <div className={styles.crawlAllErrorMessage}>{crawlAllError}</div>
          </div>
          <button type="button" className={styles.triggerBtn} onClick={handleCopyCrawlAllError}>
            コピー
          </button>
        </div>
      )}

      {crawlAllLog.length > 0 && (
        <div className={styles.crawlLogPanel}>
          <div className={styles.crawlLogHeader}>順次クロール実行ログ ({crawlAllLog.length}件)</div>
          <ul className={styles.crawlLogList}>
            {crawlAllLog.map((entry, i) => (
              <li key={`${entry.id}-${i}`} className={styles.crawlLogItem}>
                {entry.officialUrl ? (
                  <a
                    href={entry.officialUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.crawlLogName}
                  >
                    {entry.name} ↗
                  </a>
                ) : (
                  <span className={styles.crawlLogName}>{entry.name}</span>
                )}
                <span className={styles.crawlLogResult}>{entry.result}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {message && <div className={styles.message}>{message}</div>}

      {/* 実行ログ — cron/手動ジョブのイベントログ (job_events) */}
      <div className={styles.jobLogSection}>
        <button
          type="button"
          className={styles.jobLogToggle}
          onClick={() => setShowJobLog((v) => !v)}
        >
          {showJobLog ? "▾" : "▸"} 実行ログ
        </button>
        {showJobLog && (
          <div className={styles.crawlLogPanel}>
            <div className={styles.jobLogControls}>
              {(
                [
                  { value: "", label: "すべて" },
                  { value: "url_discovery", label: "URL取得" },
                  { value: "crawl", label: "クロール" },
                  { value: "edinet_sync", label: "EDINET財務" },
                  { value: "gap_sync", label: "漏れURL取得" },
                ] as const
              ).map(({ value, label }) => (
                <button
                  key={value}
                  type="button"
                  className={`${styles.colToggleBtn} ${jobEventType === value ? styles.colToggleBtnActive : ""}`}
                  onClick={() => changeJobEventType(value)}
                >
                  {label}
                </button>
              ))}
              <button type="button" className={styles.colToggleBtn} onClick={() => loadJobEvents()}>
                {jobEventsLoading ? "更新中..." : "更新"}
              </button>
              <span className={styles.jobLogNote}>30秒ごとに自動更新・直近100件</span>
            </div>
            {jobEventsError && <div className={styles.jobLogEmpty}>エラー: {jobEventsError}</div>}
            {jobEvents.length === 0 && !jobEventsLoading && !jobEventsError ? (
              <div className={styles.jobLogEmpty}>ログはまだありません</div>
            ) : (
              <ul className={styles.jobLogList}>
                {jobEvents.map((ev) => (
                  <li
                    key={ev.id}
                    className={`${styles.jobLogItem} ${ev.status === "error" ? styles.jobLogItemError : ""}`}
                  >
                    <div className={styles.jobLogItemContent}>
                      <div className={styles.jobLogItemMeta}>
                        <span>
                          {new Date(ev.createdAt).toLocaleString("ja-JP", {
                            month: "2-digit",
                            day: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                            second: "2-digit",
                          })}
                        </span>
                        <span>{jobTypeLabel(ev.jobType)}</span>
                        <span>{ev.status === "success" ? "成功" : "エラー"}</span>
                        <span className={styles.jobLogItemCompany}>
                          {ev.companyName ?? ev.companyId ?? "-"}
                        </span>
                      </div>
                      <div
                        className={`${styles.jobLogItemMessage} ${ev.status === "error" ? styles.jobLogItemMessageError : ""}`}
                      >
                        {ev.message}
                      </div>
                      {ev.jobType === "crawl" && ev.officialUrl && (
                        <a
                          href={ev.officialUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={styles.benefitUrl}
                        >
                          {ev.status === "success" ? "優待詳細ページ ↗" : `${ev.officialUrl} ↗`}
                        </a>
                      )}
                    </div>
                    {ev.status === "error" && (
                      <button
                        type="button"
                        className={styles.jobLogCopyBtn}
                        title="ログをコピー"
                        aria-label="ログをコピー"
                        onClick={() => handleCopyJobEvent(ev)}
                      >
                        <ClipboardIcon />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {/* Table */}
      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              {colVisible.name && <th>会社名</th>}
              {colVisible.benefit && <th>優待情報</th>}
              {colVisible.financials && <th>財務(EDINET概算)</th>}
              {colVisible.flags && <th>フラグ</th>}
              {colVisible.edit && <th>URL編集</th>}
            </tr>
          </thead>
          <tbody>
            {loading && items.length === 0 ? (
              <tr>
                <td colSpan={totalCols} className={styles.loadingCell}>
                  読み込み中...
                </td>
              </tr>
            ) : (
              items.map((company) => (
                <Fragment key={company.id}>
                  <tr className={getRowClass(company, styles)}>
                    {colVisible.name && (
                      <td className={styles.nameCell}>
                        <div className={styles.nameMeta}>
                          <span className={styles.nameCode}>{company.id}</span>
                        </div>
                        {company.name}
                      </td>
                    )}
                    {colVisible.benefit && (
                      <td className={styles.benefitCell}>
                        {company.officialUrl && (
                          <a
                            href={company.officialUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.benefitUrl}
                          >
                            {company.officialUrl} ↗
                          </a>
                        )}
                        <BenefitCell
                          title={company.benefitTitle}
                          description={company.benefitDescription}
                          businessDescription={company.businessDescription}
                          businessDescriptionUnclear={company.businessDescriptionUnclear}
                          minimumShares={company.minimumShares}
                          minimumBenefitValue={company.minimumBenefitValue}
                          holdingYearsJson={company.holdingYearsJson}
                          lastChangedAt={company.benefitLastChangedAt}
                          categoriesJson={company.benefitCategories}
                          recordMonthsJson={company.benefitRecordMonths}
                          deliveryMonthsJson={company.benefitDeliveryMonths}
                          tiersJson={company.benefitTiersJson}
                          relevanceScore={company.benefitRelevanceScore}
                          confirmedYearsJson={company.confirmedYearsJson}
                        />
                      </td>
                    )}
                    {colVisible.financials && (
                      <td>
                        {company.approxStockPrice == null ? (
                          <span className={styles.jobLogNote}>未取得</span>
                        ) : (
                          <div className={styles.financialsCell}>
                            <div>
                              概算株価: {Math.round(company.approxStockPrice).toLocaleString()}円
                            </div>
                            {company.marketCap != null && (
                              <div>
                                時価総額:{" "}
                                {Math.round(company.marketCap / 1_0000_0000).toLocaleString()}億円
                                {(() => {
                                  const tier = marketCapTierFromValue(company.marketCap)
                                  return tier ? `（${MARKET_CAP_TIER_LABELS[tier]}）` : null
                                })()}
                              </div>
                            )}
                            {company.dividendYield != null && (
                              <div>配当利回り: {company.dividendYield.toFixed(2)}%</div>
                            )}
                            {formatFiscalPeriodEnd(company.fiscalPeriodEnd) && (
                              <div className={styles.jobLogNote}>
                                {formatFiscalPeriodEnd(company.fiscalPeriodEnd)}
                              </div>
                            )}
                            {company.financialsSyncedAt != null && (
                              <div className={styles.jobLogNote}>
                                (取得:{" "}
                                {new Date(company.financialsSyncedAt).toLocaleDateString("ja-JP")})
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                    )}
                    {colVisible.flags && (
                      <td>
                        <div className={styles.flagCell}>
                          <button
                            type="button"
                            className={`${styles.flagBtn} ${company.noUrl ? styles.flagBtnActive : ""}`}
                            onClick={() => handleToggleNoUrl(company)}
                          >
                            URLなし
                          </button>
                          <button
                            type="button"
                            className={`${styles.flagBtn} ${company.noBenefit ? styles.flagBtnActive : ""}`}
                            onClick={() => handleToggleNoBenefit(company)}
                          >
                            優待なし
                          </button>
                          <button
                            type="button"
                            className={`${styles.flagBtn} ${company.isDiscontinued ? styles.flagBtnDiscontinued : ""}`}
                            onClick={() => handleToggleDiscontinued(company)}
                          >
                            {company.isDiscontinued
                              ? `廃止${
                                  company.discontinuedYear || company.discontinuedMonth
                                    ? `(${company.discontinuedYear ?? ""}${company.discontinuedMonth ? `/${company.discontinuedMonth}月` : ""})`
                                    : ""
                                }`
                              : "廃止"}
                          </button>
                          {company.needsReview && (
                            <button
                              type="button"
                              className={`${styles.flagBtn} ${styles.flagBtnDiscontinued}`}
                              title="複数の優待制度が併記されている可能性があります。確認後クリックで解除できます"
                              onClick={() => handleToggleNeedsReview(company)}
                            >
                              要確認
                            </button>
                          )}
                          {company.businessDescriptionUnclear && (
                            <button
                              type="button"
                              className={`${styles.flagBtn} ${styles.flagBtnDiscontinued}`}
                              title="AIがこの会社の事業内容を十分に把握できませんでした。確認後クリックで解除できます"
                              onClick={() => handleToggleBusinessDescriptionUnclear(company)}
                            >
                              事業内容不明
                            </button>
                          )}
                          {!company.officialUrl && company.externalBenefitUrlFailedAt && (
                            <span
                              className={`${styles.flagBtn} ${styles.flagBtnDiscontinued}`}
                              title="他サイトでは優待ありだが、自動URL取得に失敗しました。候補を見る・手動入力・再検索で対応してください"
                            >
                              外部優待URL不可
                            </span>
                          )}
                          {!company.officialUrl && company.urlSearchFailedAt && (
                            <button
                              type="button"
                              className={`${styles.flagBtn} ${styles.flagBtnActive}`}
                              title="自動検索で見つかりませんでした。クリックで再検索します"
                              onClick={() => handleRetrySearch(company.id)}
                            >
                              未検出 (再検索)
                            </button>
                          )}
                          {company.retryExhausted && (
                            <span
                              className={`${styles.flagBtn} ${styles.flagBtnStatic}`}
                              title="エラーからの自動再クロールも失敗したため、以降の自動再試行は停止しています。再クロールボタンで手動リセットできます"
                            >
                              自動再試行停止
                            </span>
                          )}
                        </div>
                        {editingId !== company.id && (
                          <div className={styles.flagActions}>
                            <button
                              type="button"
                              className={styles.candidateBtn}
                              disabled={candidateSearchingId === company.id}
                              onClick={() =>
                                candidates?.companyId === company.id
                                  ? setCandidates(null)
                                  : handleSearchUrls(company.id)
                              }
                            >
                              {candidateSearchingId === company.id
                                ? "検索中…"
                                : candidates?.companyId === company.id
                                  ? "閉じる"
                                  : "候補を見る"}
                            </button>
                            {company.officialUrl && (
                              <button
                                type="button"
                                className={`${styles.crawlBtn} ${crawlingIds.has(company.id) ? styles.crawlBtnActive : ""}`}
                                disabled={crawlingIds.has(company.id)}
                                onClick={() => handleTriggerOne(company.id)}
                              >
                                {crawlingIds.has(company.id) ? "実行中…" : "再クロール"}
                              </button>
                            )}
                            {company.officialUrl && (
                              <button
                                type="button"
                                className={styles.candidateBtn}
                                disabled={crawlDebugLoadingId === company.id}
                                onClick={() => handleDebugCrawl(company.id)}
                              >
                                {crawlDebugLoadingId === company.id
                                  ? "デバッグ中…"
                                  : crawlDebug?.companyId === company.id
                                    ? "閉じる"
                                    : "デバッグ"}
                              </button>
                            )}
                            <StatusBadge
                              status={company.crawlStatus}
                              errorCount={company.errorCount}
                              lastError={company.lastError}
                              crawling={crawlingIds.has(company.id)}
                            />
                          </div>
                        )}
                      </td>
                    )}
                    {colVisible.edit && (
                      <td>
                        <div className={styles.actionCell}>
                          {editingId === company.id ? (
                            <>
                              <button
                                type="button"
                                className={styles.saveBtn}
                                disabled={saving}
                                onClick={() => handleSaveUrl(company.id)}
                              >
                                保存
                              </button>
                              <button
                                type="button"
                                className={styles.cancelBtn}
                                onClick={() => setEditingId(null)}
                              >
                                キャンセル
                              </button>
                            </>
                          ) : (
                            <button
                              type="button"
                              className={styles.editBtn}
                              onClick={() => {
                                setEditingId(company.id)
                                setEditUrl(company.officialUrl ?? "")
                              }}
                            >
                              URL編集
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                  {candidates?.companyId === company.id && (
                    <CandidatesRow
                      results={candidates.results}
                      debug={candidates.debug}
                      aiRecommended={candidates.aiRecommended}
                      rawExaResponse={candidates.rawExaResponse}
                      companyName={company.name}
                      colSpan={totalCols}
                      onSelect={(url) => handleSelectCandidate(company.id, url)}
                    />
                  )}
                  {crawlDebug?.companyId === company.id && (
                    <CrawlDebugRow result={crawlDebug.result} colSpan={totalCols} />
                  )}
                </Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className={styles.pagination}>
          <button
            type="button"
            className={styles.pageBtn}
            disabled={page === 1}
            onClick={() => changePage(page - 1)}
          >
            前へ
          </button>
          <span className={styles.pageInfo}>
            {page} / {totalPages}
          </span>
          <button
            type="button"
            className={styles.pageBtn}
            disabled={page === totalPages}
            onClick={() => changePage(page + 1)}
          >
            次へ
          </button>
        </div>
      )}
    </div>
  )
}

// Mirrors apps/worker/src/infra/aiExtractor.ts's PRICE_PER_MILLION_USD
// (Claude Haiku 4.5, 5-minute ephemeral cache TTL) — kept in sync manually
// since the worker and web app don't share a runtime module here.
function estimateCostUsd(usage: {
  inputTokens: number
  outputTokens: number
  cacheReadTokens: number
  cacheCreationTokens: number
}): number {
  return (
    (usage.inputTokens * 1.0 +
      usage.outputTokens * 5.0 +
      usage.cacheCreationTokens * 1.25 +
      usage.cacheReadTokens * 0.1) /
    1_000_000
  )
}

const CATEGORY_LABELS: Record<string, string> = {
  food: "食品・飲料",
  dining: "外食",
  travel: "旅行・宿泊",
  discount: "割引券",
  quo: "QUO/商品券",
  catalog: "カタログ",
  digital: "デジタルギフト",
  own: "自社製品",
  donation: "寄付",
  tour: "施設見学",
  other: "その他",
}

function BenefitCell({
  title,
  description,
  businessDescription,
  businessDescriptionUnclear,
  minimumShares,
  minimumBenefitValue,
  holdingYearsJson,
  lastChangedAt,
  categoriesJson,
  recordMonthsJson,
  deliveryMonthsJson,
  tiersJson,
  relevanceScore,
  confirmedYearsJson,
}: {
  title: string | null
  description: string | null
  businessDescription: string | null
  businessDescriptionUnclear: boolean
  minimumShares: number | null
  minimumBenefitValue: number | null
  holdingYearsJson: string | null
  lastChangedAt: number | null
  categoriesJson: string | null
  recordMonthsJson: string | null
  deliveryMonthsJson: string | null
  tiersJson: string | null
  relevanceScore: number | null
  confirmedYearsJson: string | null
}) {
  if (!title) return <span className={styles.noUrl}>未取得</span>

  const categories: string[] = categoriesJson ? (JSON.parse(categoriesJson) as string[]) : []
  const recordMonths: number[] = recordMonthsJson ? (JSON.parse(recordMonthsJson) as number[]) : []
  const deliveryMonths: number[] = deliveryMonthsJson
    ? (JSON.parse(deliveryMonthsJson) as number[])
    : []
  const tiers: { minimumShares: number; description: string }[] = tiersJson
    ? (JSON.parse(tiersJson) as { minimumShares: number; description: string }[])
    : []
  const holdingYears: number[] = holdingYearsJson ? (JSON.parse(holdingYearsJson) as number[]) : []
  const date = lastChangedAt ? new Date(lastChangedAt).toLocaleDateString("ja-JP") : null
  const relevanceLevel = relevanceLevelFromScore(relevanceScore ?? 0)
  const confirmedYears: number[] = confirmedYearsJson
    ? (JSON.parse(confirmedYearsJson) as number[])
    : []

  return (
    <div className={styles.benefitDetail}>
      <div className={styles.benefitTitleRow}>
        <span className={styles.benefitTitle}>{title}</span>
        {minimumShares != null && (
          <span className={styles.benefitShares}>{minimumShares.toLocaleString()}株以上</span>
        )}
      </div>
      {(businessDescription || businessDescriptionUnclear) && (
        <div className={styles.benefitRow}>
          <span className={styles.benefitLabel}>事業内容</span>
          <span>
            {businessDescription || "(未取得)"}
            {businessDescriptionUnclear && (
              <span className={styles.businessDescriptionUnclearBadge}>事業内容不明</span>
            )}
          </span>
        </div>
      )}
      {categories.length > 0 && (
        <div className={styles.benefitRow}>
          <span className={styles.benefitLabel}>カテゴリ</span>
          <span>{categories.map((c) => CATEGORY_LABELS[c] ?? c).join(" / ")}</span>
        </div>
      )}
      {recordMonths.length > 0 && (
        <div className={styles.benefitRow}>
          <span className={styles.benefitLabel}>権利確定</span>
          <span>{recordMonths.join("・")}月</span>
        </div>
      )}
      {deliveryMonths.length > 0 && (
        <div className={styles.benefitRow}>
          <span className={styles.benefitLabel}>送付月</span>
          <span>{formatDeliveryMonths(deliveryMonths)}</span>
        </div>
      )}
      {minimumBenefitValue != null && (
        <div className={styles.benefitRow}>
          <span className={styles.benefitLabel}>優待金額</span>
          <span>{minimumBenefitValue.toLocaleString()}円〜</span>
        </div>
      )}
      {holdingYears.length > 0 && (
        <div className={styles.benefitRow}>
          <span className={styles.benefitLabel}>長期保有</span>
          <span>{holdingYears.map((y) => `${y}年以上`).join("・")}から特典</span>
        </div>
      )}
      {description && (
        <div className={styles.benefitRow}>
          <span className={styles.benefitLabel}>内容</span>
          <span>{description}</span>
        </div>
      )}
      {relevanceLevel && (
        <div className={styles.benefitRow}>
          <span className={styles.benefitLabel}>事業関連度</span>
          <span>{RELEVANCE_LEVEL_LABELS[relevanceLevel]}</span>
        </div>
      )}
      {confirmedYears.length > 0 && (
        <div className={styles.benefitRow}>
          <span className={styles.benefitLabel}>確認済み年</span>
          <span>{confirmedYears.join("・")}</span>
        </div>
      )}
      {tiers.length > 0 && (
        <div className={styles.benefitRow}>
          <span className={styles.benefitLabel}>段階</span>
          <ul className={styles.benefitTiersList}>
            {tiers.map((t, i) => (
              <li key={`${t.minimumShares}-${i}`}>
                {t.minimumShares != null ? `${t.minimumShares.toLocaleString()}株以上` : "株数不明"}
                : {t.description}
              </li>
            ))}
          </ul>
        </div>
      )}
      {date && <div className={styles.benefitDate}>{date} 更新</div>}
    </div>
  )
}

function CrawlDebugRow({
  result,
  colSpan,
}: {
  result: CrawlDebugResult | null
  colSpan: number
}) {
  if (!result) {
    return (
      <tr>
        <td colSpan={colSpan} className={styles.candidatesCell}>
          <p className={styles.candidatesEmpty}>デバッグ情報の取得に失敗しました</p>
        </td>
      </tr>
    )
  }

  return (
    <tr>
      <td colSpan={colSpan} className={styles.candidatesCell}>
        {result.fetchError ? (
          <p className={styles.candidatesEmpty}>フェッチ失敗: {result.fetchError}</p>
        ) : (
          <>
            {result.looksBlocked && (
              <p className={styles.candidatesEmpty}>
                ⚠
                アクセス拒否/チャレンジページの可能性があります(抽出テキストが短すぎるか、ブロックパターンに一致)
              </p>
            )}
            {result.aiExtraction?.hasMultipleConditions && (
              <p className={styles.candidatesEmpty}>
                ⚠ 複数の優待制度が併記されている可能性があると判定されました(要確認フラグ対象)
              </p>
            )}
            <p className={styles.candidatesDebug}>
              抽出テキスト長: {result.extractedTextLength}文字
              {result.aiFailed && " ・ AI抽出失敗 (レスポンス無しまたはパース失敗)"}
            </p>
            {result.aiUsage && (
              <p className={styles.candidatesDebug}>
                トークン使用量: 入力{result.aiUsage.inputTokens} / 出力
                {result.aiUsage.outputTokens}
                {result.aiUsage.cacheReadTokens > 0
                  ? ` ・ キャッシュ読込${result.aiUsage.cacheReadTokens} (キャッシュ命中)`
                  : result.aiUsage.cacheCreationTokens > 0
                    ? ` ・ キャッシュ書込${result.aiUsage.cacheCreationTokens} (今回キャッシュ作成)`
                    : " ・ キャッシュ未使用"}
                {` ・ 約$${estimateCostUsd(result.aiUsage).toFixed(4)}`}
              </p>
            )}
            <p className={styles.candidatesDebug}>AI抽出結果:</p>
            <pre className={styles.crawlDebugPre}>
              {result.aiExtraction ? JSON.stringify(result.aiExtraction, null, 2) : "(なし)"}
            </pre>
            <p className={styles.candidatesDebug}>AIに送ったテキスト:</p>
            <pre className={styles.crawlDebugPre}>{result.extractedText}</pre>
          </>
        )}
      </td>
    </tr>
  )
}

const CANDIDATE_EXCLUDED_DOMAINS = ["finance.yahoo.co.jp", "yuhai.jp", "yutai-guide.daiwair.co.jp"]

function CandidatesRow({
  results,
  debug,
  aiRecommended,
  rawExaResponse,
  companyName,
  colSpan,
  onSelect,
}: {
  results: { title: string; url: string }[]
  debug: string
  aiRecommended: string | null
  rawExaResponse?: string
  companyName: string
  colSpan: number
  onSelect: (url: string) => void
}) {
  const filtered = results.filter((r) => !CANDIDATE_EXCLUDED_DOMAINS.some((d) => r.url.includes(d)))
  const googleUrl = `https://www.google.com/search?q=${encodeURIComponent(`${companyName} 株主優待`)}`
  return (
    <tr>
      <td colSpan={colSpan} className={styles.candidatesCell}>
        <p className={styles.candidatesDebug}>{debug}</p>
        {filtered.length === 0 ? (
          <>
            <p className={styles.candidatesEmpty}>
              候補が見つかりませんでした —{" "}
              <a href={googleUrl} target="_blank" rel="noopener noreferrer">
                Googleで検索
              </a>
            </p>
            {rawExaResponse && (
              <pre
                className={styles.candidatesDebug}
                style={{
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-all",
                  maxHeight: 200,
                  overflowY: "auto",
                }}
              >
                {rawExaResponse}
              </pre>
            )}
          </>
        ) : (
          <ul className={styles.candidatesList}>
            {filtered.map((r) => (
              <li
                key={r.url}
                className={`${styles.candidateItem} ${r.url === aiRecommended ? styles.candidateItemRecommended : ""}`}
              >
                <div className={styles.candidateInfo}>
                  <div className={styles.candidateTitleRow}>
                    <span className={styles.candidateTitle}>{r.title}</span>
                    {r.url === aiRecommended && (
                      <span className={styles.aiRecommendedBadge}>AI推薦</span>
                    )}
                  </div>
                  <a
                    href={r.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.candidateUrl}
                  >
                    {r.url}
                  </a>
                </div>
                <button
                  type="button"
                  className={styles.candidateSelectBtn}
                  onClick={() => onSelect(r.url)}
                >
                  登録
                </button>
              </li>
            ))}
          </ul>
        )}
      </td>
    </tr>
  )
}

function StatusBadge({
  status,
  errorCount,
  lastError,
  crawling,
}: {
  status: string | null
  errorCount: number
  lastError: string | null
  crawling?: boolean
}) {
  const [open, setOpen] = useState(false)

  if (!status) return <span className={styles.statusNone}>-</span>
  const map: Record<string, string> = {
    pending: styles.statusPending,
    done: styles.statusDone,
    error: styles.statusError,
  }
  const labels: Record<string, string> = {
    pending: "待機中",
    done: "完了",
    error: `エラー(${errorCount})`,
  }
  const clickable = status === "error" && !!lastError
  return (
    <div>
      <span
        className={`${styles.statusBadge} ${map[status] ?? ""} ${clickable ? styles.statusBadgeClickable : ""} ${crawling ? styles.statusCrawling : ""}`}
        onClick={() => clickable && setOpen((v) => !v)}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && clickable && setOpen((v) => !v)}
        role={clickable ? "button" : undefined}
        tabIndex={clickable ? 0 : undefined}
      >
        {crawling ? "⟳ クロール中" : (labels[status] ?? status)}
      </span>
      {open && lastError && <div className={styles.errorDetail}>{lastError}</div>}
    </div>
  )
}
